/**
 * Responsibility: hold the bridge archetypes and turn one bridge's parameters into line segments.
 *
 * ## A registry, like the city's buildings, and for the same reason
 *
 * One bridge shape would be a prop. Several make crossings something to look at, and the choice between them is not
 * arbitrary here — **span decides the type**, exactly as it does in real engineering. A short crossing gets an arch; a
 * long one needs a suspension bridge because an arch that wide would be absurd. So there is no weighted pool to tune.
 *
 * ## Segments in the same shape the buildings use
 *
 * Each archetype returns `{x1,y1,z1,x2,y2,z2,t1,t2}` records, which is precisely what `emitShapeSegments` already
 * consumes for every building in the world. **So bridges needed no new emitter.** `t` is a height fraction driving the
 * gradient, as everywhere else.
 *
 * ## The arch is deliberately flat
 *
 * Nothing here should look like the rock arches in the badlands. Those are tall and round because erosion made them; a
 * bridge arch is shallow because it carries a level road. `BRIDGE.ARCH_RISE_RATIO` is the dial and it is set low.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { BRIDGE } from '../config.js';

/**
 * The frame a bridge is built in: a point on the deck, and the two directions that matter.
 *
 * @param {object} bridge
 * @returns {{along: [number, number], across: [number, number], at: Function}}
 */
function frameOf(bridge) {
  const along = [Math.cos(bridge.heading), Math.sin(bridge.heading)];
  const across = [-along[1], along[0]];

  /**
   * A point on the bridge, from a fraction along its span, an offset across it, and a height.
   *
   * @param {number} t - 0 at one abutment, 1 at the other.
   * @param {number} side - Offset across the deck, in world units.
   * @param {number} y
   */
  const at = (t, side, y) => {
    const distance = (t - 0.5) * bridge.span;
    return [
      bridge.x + along[0] * distance + across[0] * side,
      y,
      bridge.z + along[1] * distance + across[1] * side,
    ];
  };

  return { along, across, at };
}

/** A segment record in the shape `emitShapeSegments` consumes. */
function line([x1, y1, z1], [x2, y2, z2], t1, t2) {
  return { x1, y1, z1, x2, y2, z2, t1, t2: t2 ?? t1 };
}

/**
 * The deck: two edge beams and a rung between them at every stop.
 *
 * Shared by every archetype, because a bridge is a level road whatever holds it up. The rungs are what make it read as a
 * deck rather than as two parallel lines.
 */
function deckSegments(bridge, height) {
  const { at } = frameOf(bridge);
  const half = BRIDGE.DECK_WIDTH / 2;
  const stops = BRIDGE.DECK_STOPS;
  const segments = [];

  for (let side of [-half, half]) {
    for (let i = 0; i < stops - 1; i += 1) {
      segments.push(line(at(i / (stops - 1), side, bridge.deckY), at((i + 1) / (stops - 1), side, bridge.deckY), height));
    }
  }

  for (let i = 0; i < stops; i += 1) {
    const t = i / (stops - 1);
    segments.push(line(at(t, -half, bridge.deckY), at(t, half, bridge.deckY), height));
  }

  return segments;
}

/** Height fraction for a y, against the bridge's own extent, so the gradient runs bottom to top. */
function fractionFor(bridge, y) {
  const bottom = Math.min(bridge.groundY, bridge.deckY - bridge.span * BRIDGE.ARCH_RISE_RATIO);
  const top = bridge.deckY + bridge.span * BRIDGE.TOWER_HEIGHT_RATIO;
  return Math.min(1, Math.max(0, (y - bottom) / Math.max(1, top - bottom)));
}

/**
 * A shallow arch bridge: two ribs under the deck, braced across, with posts up to the road.
 *
 * The rise is a small fraction of the span on purpose — see the note at the top of this file about not looking like the
 * badlands rock arches.
 */
const archBridge = {
  id: 'arch',
  label: 'Arch bridge',

  build(bridge) {
    const { at } = frameOf(bridge);
    const half = BRIDGE.DECK_WIDTH / 2;
    const stops = BRIDGE.ARCH_STOPS;

    const crown = bridge.deckY - BRIDGE.ARCH_DECK_GAP;
    const rise = Math.min(bridge.span * BRIDGE.ARCH_RISE_RATIO, crown - bridge.groundY);
    const spring = crown - rise;

    /** Parabolic rib: springs from the abutments and peaks just under the middle of the deck. */
    const ribY = (t) => spring + rise * (1 - Math.pow(2 * t - 1, 2));

    const segments = deckSegments(bridge, fractionFor(bridge, bridge.deckY));

    for (const side of [-half, half]) {
      for (let i = 0; i < stops - 1; i += 1) {
        const a = i / (stops - 1);
        const b = (i + 1) / (stops - 1);
        segments.push(
          line(at(a, side, ribY(a)), at(b, side, ribY(b)), fractionFor(bridge, ribY(a)), fractionFor(bridge, ribY(b))),
        );
      }
    }

    for (let i = 0; i < stops; i += 1) {
      const t = i / (stops - 1);
      const y = ribY(t);
      const fraction = fractionFor(bridge, y);

      // Brace between the ribs, and a post from each rib up to the deck.
      segments.push(line(at(t, -half, y), at(t, half, y), fraction));
      segments.push(line(at(t, -half, y), at(t, -half, bridge.deckY), fraction, fractionFor(bridge, bridge.deckY)));
      segments.push(line(at(t, half, y), at(t, half, bridge.deckY), fraction, fractionFor(bridge, bridge.deckY)));
    }

    return segments;
  },
};

/**
 * A suspension bridge: two towers, a sagging main cable, and hangers down to the deck.
 *
 * **The most distinctive silhouette available to this world.** Nothing else here has a curve hanging in tension, and in
 * wireframe a catenary between two towers reads instantly. Reserved for the widest crossings, which is both the honest
 * engineering reason and what stops it becoming a repeated prop.
 */
const suspensionBridge = {
  id: 'suspension',
  label: 'Suspension bridge',

  build(bridge) {
    const { at } = frameOf(bridge);
    const half = BRIDGE.DECK_WIDTH / 2;
    const deckFraction = fractionFor(bridge, bridge.deckY);

    const towerHeight = bridge.span * BRIDGE.TOWER_HEIGHT_RATIO;
    const towerTop = bridge.deckY + towerHeight;
    const sag = towerHeight * BRIDGE.CABLE_SAG_RATIO;
    const inset = BRIDGE.TOWER_INSET;

    const segments = deckSegments(bridge, deckFraction);

    // Towers: a leg each side of the deck, a beam across the top, and one at deck level.
    for (const t of [inset, 1 - inset]) {
      for (const side of [-half, half]) {
        segments.push(
          line(at(t, side, bridge.groundY), at(t, side, towerTop), fractionFor(bridge, bridge.groundY), 1),
        );
      }
      segments.push(line(at(t, -half, towerTop), at(t, half, towerTop), 1));
      segments.push(line(at(t, -half, bridge.deckY), at(t, half, bridge.deckY), deckFraction));
    }

    /*
      Main cable, sagging between the towers. A parabola rather than a true catenary: the two are visually
      indistinguishable at this sag ratio, and the parabola needs no hyperbolic functions.
    */
    const cableY = (t) => {
      const u = (t - inset) / (1 - 2 * inset);
      return towerTop - sag * (1 - Math.pow(2 * u - 1, 2));
    };

    const stops = BRIDGE.CABLE_STOPS;

    for (const side of [-half, half]) {
      // Backstays from the deck ends up to the tower tops.
      segments.push(line(at(0, side, bridge.deckY), at(inset, side, towerTop), deckFraction, 1));
      segments.push(line(at(1, side, bridge.deckY), at(1 - inset, side, towerTop), deckFraction, 1));

      for (let i = 0; i < stops - 1; i += 1) {
        const a = inset + ((1 - 2 * inset) * i) / (stops - 1);
        const b = inset + ((1 - 2 * inset) * (i + 1)) / (stops - 1);
        segments.push(
          line(at(a, side, cableY(a)), at(b, side, cableY(b)), fractionFor(bridge, cableY(a)), fractionFor(bridge, cableY(b))),
        );
      }

      /*
        Hangers, evenly spaced between the towers. Spaced strictly *inside* the towers so none of them lands on a tower
        and collapses to a zero-length segment against it.
      */
      for (let i = 0; i < BRIDGE.HANGERS; i += 1) {
        const t = inset + ((1 - 2 * inset) * (i + 1)) / (BRIDGE.HANGERS + 1);
        segments.push(
          line(at(t, side, cableY(t)), at(t, side, bridge.deckY), fractionFor(bridge, cableY(t)), deckFraction),
        );
      }
    }

    return segments;
  },
};

/** Every bridge archetype, keyed by id. */
export const BRIDGE_SHAPES = { arch: archBridge, suspension: suspensionBridge };

/**
 * The archetype a span calls for.
 *
 * **Span decides, not a random roll.** A suspension bridge over a 250-unit stream would be silly and an arch over a
 * 900-unit one would be impossible, so the rule that makes engineering sense also removes a dial that would need tuning.
 *
 * @param {number} span - World units, abutment to abutment.
 * @returns {string} An id in `BRIDGE_SHAPES`.
 */
export function archetypeForSpan(span) {
  return span >= BRIDGE.SUSPENSION_MIN_SPAN ? 'suspension' : 'arch';
}

/**
 * The archetype a bridge uses, falling back rather than throwing on an unknown name.
 *
 * Matches how the aircraft and building registries degrade, so a renamed archetype in an old save cannot empty a river
 * of its crossings.
 *
 * @param {string} id
 * @returns {object}
 */
export function bridgeShapeOrDefault(id) {
  return BRIDGE_SHAPES[id] ?? archBridge;
}
